/**
 * Calendar Utilities
 * Time parsing, conflict detection, and schedule building for course calendar
 */

/**
 * Parse course meeting times from database format
 * Input: start_times array like ["1900-01-01 10:30:00", null, "1900-01-01 10:30:00", ...]
 * Output: {monday: {start: "10:30", end: "11:45"}, wednesday: {...}}
 */
export function parseCourseSchedule(course) {
  const schedule = {};

  // Parse days from JSON string if needed
  let days = course.days || [];
  if (typeof days === 'string') {
    try {
      days = JSON.parse(days);
    } catch (e) {
      days = [];
    }
  }

  // Parse start_times from JSON string if needed
  let startTimes = course.start_times || [];
  if (typeof startTimes === 'string') {
    try {
      startTimes = JSON.parse(startTimes);
    } catch (e) {
      startTimes = [];
    }
  }

  // Parse end_times from JSON string if needed
  let endTimes = course.end_times || [];
  if (typeof endTimes === 'string') {
    try {
      endTimes = JSON.parse(endTimes);
    } catch (e) {
      endTimes = [];
    }
  }

  const dayMap = {
    'M': 'monday',
    'T': 'tuesday',
    'W': 'wednesday',
    'Th': 'thursday',
    'F': 'friday',
    'S': 'saturday',
    'Su': 'sunday'
  };

  // Match days with their corresponding times
  // The times array has entries at specific indices corresponding to days of week
  // [Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday]
  const dayIndexMap = {
    'Su': 0,
    'M': 1,
    'T': 2,
    'W': 3,
    'Th': 4,
    'F': 5,
    'S': 6
  };

  days.forEach((day) => {
    if (!day) return;

    const dayName = dayMap[day];
    if (!dayName) return;

    // Find the time at the correct index for this day
    const dayIndex = dayIndexMap[day];
    if (dayIndex === undefined) return;

    const startTime = startTimes[dayIndex];
    const endTime = endTimes[dayIndex];

    if (startTime && endTime && startTime !== 'null' && endTime !== 'null') {
      const start = extractTime(startTime);
      const end = extractTime(endTime);

      if (start && end) {
        schedule[dayName] = { start, end };
      }
    }
  });

  return schedule;
}

/**
 * Extract time string from datetime
 * "1900-01-01 10:30:00" -> "10:30"
 */
function extractTime(dateTimeStr) {
  if (!dateTimeStr || typeof dateTimeStr !== 'string') return null;

  const timeMatch = dateTimeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (timeMatch) {
    const hour = timeMatch[1].padStart(2, '0');
    const minute = timeMatch[2];
    return `${hour}:${minute}`;
  }

  return null;
}

/**
 * Convert time string to minutes since midnight
 * "10:30" -> 630
 */
export function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Check if two time ranges overlap
 */
export function timesOverlap(start1, end1, start2, end2) {
  const s1 = timeToMinutes(start1);
  const e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  const e2 = timeToMinutes(end2);

  // Ranges overlap if: start1 < end2 AND start2 < end1
  return s1 < e2 && s2 < e1;
}

/**
 * Detect conflicts between a new course and existing planned courses
 * Returns array of conflicting courses
 */
export function detectConflicts(newCourse, existingCourses) {
  const conflicts = [];
  const newSchedule = parseCourseSchedule(newCourse);

  existingCourses.forEach(existing => {
    const existingSchedule = parseCourseSchedule(existing);

    // Check each day for overlaps
    Object.keys(newSchedule).forEach(day => {
      if (existingSchedule[day]) {
        const newSlot = newSchedule[day];
        const existingSlot = existingSchedule[day];

        if (timesOverlap(newSlot.start, newSlot.end, existingSlot.start, existingSlot.end)) {
          conflicts.push({
            course: existing,
            day: day,
            time: `${existingSlot.start}-${existingSlot.end}`
          });
        }
      }
    });
  });

  return conflicts;
}

/**
 * Format time for display
 * "10:30" -> "10:30 AM"
 */
export function formatTime(timeStr) {
  if (!timeStr) return '';

  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get time slot index for calendar grid
 * Used for positioning course blocks in calendar
 */
export function getTimeSlotIndex(timeStr, startHour = 9) {
  const minutes = timeToMinutes(timeStr);
  const startMinutes = startHour * 60;
  return (minutes - startMinutes) / 15; // 15-minute increments
}

/**
 * Calculate course block height based on duration
 * Each 15-minute slot = 1 unit height
 */
export function calculateBlockHeight(startTime, endTime) {
  const duration = timeToMinutes(endTime) - timeToMinutes(startTime);
  return Math.max(duration / 15, 1); // Minimum 1 unit
}